import type { FilterCriterion } from 'features/trend-detection/types'

/**
 * Detects the pathological config where the focus group covers every cohort
 * value along the entity dimension — leaving no outgroup to compare against.
 *
 * Mirrors `_cohort_focus_overlap_error` in the backend's
 * `routers/benchmarking_agent/use_cases.py`. Keep the rules in sync; either
 * side drifting is silent and breaks the agent.
 *
 * Returns a human-readable error string when the config is bad, or null when
 * it's OK.
 *
 * Allowed:
 *   - cohort doesn't filter the entity dimension (treated as "all values" — focus is a subset)
 *   - focus is a strict subset of cohort along the entity dimension
 *
 * Rejected:
 *   - cohort filters the entity dimension AND focus values cover every cohort value
 */
export function cohortFocusOverlapError(
  cohortFilters: FilterCriterion[],
  focusGroupFilters: FilterCriterion[],
  entityType: 'brand' | 'product_hierarchy' | null,
): string | null {
  if (entityType === null) return null

  const entityValues = (filters: FilterCriterion[]): Set<string> => {
    const out = new Set<string>()
    for (const f of filters) {
      // Canonicalize `product` → `product_hierarchy` to match the run-time
      // normalization the backend applies.
      const normalized = f.field === 'product' ? 'product_hierarchy' : f.field
      if (normalized !== entityType) continue
      const vals = f.values ?? (Array.isArray(f.value) ? f.value : [])
      for (const v of vals) out.add(v.toLowerCase())
    }
    return out
  }

  const cohortValues = entityValues(cohortFilters)
  // Cohort doesn't filter the entity dimension → treated as "all values".
  if (cohortValues.size === 0) return null

  const focusValues = entityValues(focusGroupFilters)
  // Focus covers every cohort value → empty outgroup.
  let everyCohortInFocus = true
  for (const v of cohortValues) {
    if (!focusValues.has(v)) {
      everyCohortInFocus = false
      break
    }
  }
  if (everyCohortInFocus) {
    return (
      `Focus group covers every '${entityType}' value in the cohort, ` +
      `so there's no outgroup to compare against. ` +
      `Either narrow the focus to a subset of the cohort, or broaden the cohort.`
    )
  }
  return null
}
